import { Box, Button, Dialog, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { FoodsIcon } from "@foodpilot/foods";
import { useSettingsCMSContext } from "src/context/Settings/CMS/useSettingsCMSContext";
import { PuckEditor } from "src/components/Puck/PuckEditor";
import { PuckRenderer } from "src/components/Puck/PuckRenderer";
import { Data } from "@measured/puck";
import { Props } from "src/components/Puck/config";
import { RootProps } from "src/components/Puck/config/root";
import { useTranslation } from "react-i18next";
import { CMSOldPageSettings } from "./CMSOldHomeSettings";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`cms-tabpanel-${index}`}
            {...other}
            style={{ height: value === index ? "100%" : 0 }}
        >
            {value === index && <Box sx={{ height: "100%" }}>{children}</Box>}
        </div>
    );
}

export const CMSPageSettings = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { applicationParameters } = useApplicationParameters();
    const { content, isContentFetched, pageParams } = useSettingsCMSContext();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState<Data<Props, RootProps> | null>(null);
    const [tabValue, setTabValue] = useState(0);

    if (applicationParameters === undefined) return null;

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handlePreview = (data: Data<Props, RootProps>) => {
        setPreviewData(data);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
    };

    if (!content && isContentFetched && tabValue === 0) {
        return (
            <Box sx={{ p: theme.spacing(4), textAlign: "center" }}>
                <Typography>{t("settings.cms.homepage.noContent", "No content found")}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column" }}>
            <Box
                sx={{
                    borderBottom: theme.spacing(1),
                    borderColor: "divider",
                    bgcolor: theme.palette.common.white,
                }}
            >
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{
                        padding: theme.spacing(0, 2),
                        "& .MuiTab-root": {
                            fontWeight: 600,
                            fontSize: "1rem",
                            textTransform: "none",
                        },
                    }}
                >
                    <Tab value={0} label={t("settings.cms.homepage.tab", "Puck Homepage")} />
                    <Tab value={1} label={t("settings.cms.oldhomepage.tab", "Classic Homepage")} />
                </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
                <Box sx={{ height: "100%", flex: 1 }}>
                    <PuckEditor contentId={pageParams.pageId || ""} onPreview={handlePreview} />
                </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                <CMSOldPageSettings />
            </TabPanel>

            <Dialog fullScreen open={previewOpen} onClose={handleClosePreview}>
                <Box sx={{ position: "relative", height: "100vh", overflow: "auto" }}>
                    <Button
                        variant="ternary"
                        color="primary"
                        sx={{
                            position: "fixed",
                            top: 16,
                            right: 16,
                            zIndex: 9999,
                        }}
                        onClick={handleClosePreview}
                        startIcon={<FoodsIcon icon="close" size={2} />}
                    >
                        {t("glossary.close", "Close Preview")}
                    </Button>
                    <PuckRenderer data={(previewOpen && previewData) || undefined} isLoading={!isContentFetched} />
                </Box>
            </Dialog>
        </Box>
    );
};
